/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-09-29
 */
import { Injectable, inject } from '@angular/core';
import { DirtyStateService } from './dirty-state.service';

/** Claves exactas del almacenamiento local que son estado de trabajo. */
const CLAVES_LOCALES = [
  'dirty_tabs',
  'tab_id_map',
  'current_tab_id',
  'nebula_ui_nav_v1',
  'nebula:navigation:state',
];

/** Prefijos del almacenamiento local que son estado de trabajo. */
const PREFIJOS_LOCALES = ['nebula:activeRoute:'];

/**
 * El estado de TRABAJO de un producto: lo que el usuario deja a medias mientras trabaja.
 *
 * <p>Regla (Carlos, 29-sep-2026): entrar a un producto es empezar DESDE CERO. Nada de lo que se
 * dejo abierto -pestañas, formularios en creacion o edicion, busquedas, marcas de cambios sin
 * guardar- vuelve a aparecer. Antes volvia: la pantalla guardaba su borrador al destruirse y lo
 * reponia al volver, y los stores globales seguian en memoria con su modo.</p>
 *
 * <p>Se borra el estado de trabajo y se RECARGA la aplicacion en la ruta del producto. La recarga
 * es la que garantiza el cero: vacia de una vez todos los servicios y stores en memoria, sin
 * depender de que cada uno sepa reiniciarse. Lo que NO es trabajo se conserva: la sesion, el
 * producto elegido, el idioma, el tema, el perfil visual y las preferencias del usuario.</p>
 */
@Injectable({ providedIn: 'root' })
export class EstadoDeTrabajoService {
  private readonly cambios = inject(DirtyStateService);

  /**
   * Si alguna pestaña del producto tiene cambios sin guardar, no solo la activa.
   *
   * @returns si hay algo que se perderia al salir.
   */
  hayCambiosSinGuardar(): boolean {
    return this.cambios.dirtyTabIds().length > 0;
  }

  /**
   * Entra a un producto desde cero: borra el estado de trabajo y recarga en su ruta.
   *
   * @param ruta ruta del producto, con o sin la barra inicial.
   */
  entrarDesdeCero(ruta: string): void {
    this.borrar();
    const destino = '/' + String(ruta ?? '').replace(/^\/+/, '');
    window.location.assign(destino);
  }

  /**
   * Borra el estado de trabajo del almacenamiento, sin recargar.
   *
   * <p>En `sessionStorage` son los borradores de formulario (`form_state_*`) y el estado de cada
   * pantalla (`*_state`: busqueda, orden, pagina). En `localStorage`, las pestañas, sus marcas de
   * cambios y la ruta activa de cada modulo.</p>
   */
  borrar(): void {
    for (const clave of Object.keys(sessionStorage)) {
      if (clave.startsWith('form_state_') || clave.endsWith('_state')) {
        sessionStorage.removeItem(clave);
      }
    }
    for (const clave of Object.keys(localStorage)) {
      const esDeTrabajo =
        CLAVES_LOCALES.includes(clave) ||
        PREFIJOS_LOCALES.some((prefijo) => clave.startsWith(prefijo)) ||
        clave.endsWith('_state');
      if (esDeTrabajo) localStorage.removeItem(clave);
    }
    this.cambios.clearAll();
  }
}
